/**
 * Reader self-heal for the Shadow/Purified roster.
 *
 * When the live scanner reads a Shadow or Purified Pokemon whose dex id is NOT
 * in the curated roster, the read is normally blocked (so the app never invents
 * a variant slot for a species that can't have one). But the curated roster
 * lags real releases ("official is slow"). So: when a blocked read is seen at
 * high confidence across enough distinct frames, we trust the reader, POST the
 * observation to the worker (which unions it into the roster + emails an admin
 * to verify), and let the never-shrink roster carry it forward.
 *
 * Guard rails (Conservative bar chosen by the user):
 *   - two-frame confirmed: id must appear in >= SELF_HEAL_MIN_FRAMES distinct
 *     frames before we report it, and
 *   - high confidence: the best confidence for that id must be
 *     >= SELF_HEAL_HIGH_CONFIDENCE (0-100 scale).
 *
 * This module is PURE + side-effect free except reportSelfHeal, which takes an
 * injected poster so it stays trivially testable and never throws.
 */

import type { BlockedShadowRead } from "@/lib/dexLiveFrameAdapter";

/** Best confidence (0-100) an id must reach before it can self-heal. */
export const SELF_HEAL_HIGH_CONFIDENCE = 85;
/** Distinct frames an id must be seen blocked in before it can self-heal. */
export const SELF_HEAL_MIN_FRAMES = 2;
/**
 * Confidence (0-1) we report to the worker once the bar is cleared. We clear
 * our own high bar first, so we report a fixed high value comfortably inside
 * the worker's accepted 0.85-1.0 window.
 */
export const SELF_HEAL_REPORT_CONFIDENCE = 0.95;

/** Endpoint the worker exposes for observed self-heal. */
export const SELF_HEAL_ENDPOINT = "/api/shadow-roster/observed";

/** A single self-heal report the client will POST to the worker. */
export interface SelfHealReport {
  dexId: number;
  variant: "shadow" | "purified";
  /** 0-1, always SELF_HEAL_REPORT_CONFIDENCE once the bar is cleared. */
  confidence: number;
}

/** Injected non-throwing POST helper (matches apiRequestRaw shape). */
export type SelfHealPoster = (
  method: string,
  url: string,
  data?: unknown,
) => Promise<{ ok: boolean }>;

interface Tracked {
  variant: "shadow" | "purified";
  frames: Set<number>;
  maxConfidence: number;
}

/**
 * Accumulates blocked Shadow/Purified reads across frames and drains the ones
 * that cross the confidence + frame-count bar. Never-shrink: once an id is
 * drained it is not re-reported in the same session.
 */
export class ShadowRosterSelfHealTracker {
  private tracked = new Map<number, Tracked>();
  private reported = new Set<number>();
  private frameSeq = 0;

  /**
   * Record one frame's worth of blocked reads. Each call is treated as a new
   * distinct frame for the two-frame-confirmed check.
   */
  recordFrame(blocked: readonly BlockedShadowRead[]): void {
    if (blocked.length === 0) return;
    const frameId = this.frameSeq++;
    for (const b of blocked) {
      if (!Number.isInteger(b.dexId) || b.dexId < 1) continue;
      if (b.variant !== "shadow" && b.variant !== "purified") continue;
      const existing = this.tracked.get(b.dexId);
      if (existing) {
        existing.frames.add(frameId);
        if (b.confidence > existing.maxConfidence) {
          existing.maxConfidence = b.confidence;
        }
        // Keep the most recent variant reading; both slots get created server
        // side anyway (shadow implies purified and vice versa).
        existing.variant = b.variant;
      } else {
        this.tracked.set(b.dexId, {
          variant: b.variant,
          frames: new Set([frameId]),
          maxConfidence: b.confidence,
        });
      }
    }
  }

  /**
   * Return the ids that have crossed the bar since the last drain, and mark
   * them reported so they never fire twice in one session.
   */
  drain(): SelfHealReport[] {
    const out: SelfHealReport[] = [];
    for (const [dexId, t] of Array.from(this.tracked)) {
      if (this.reported.has(dexId)) continue;
      if (
        t.frames.size >= SELF_HEAL_MIN_FRAMES &&
        t.maxConfidence >= SELF_HEAL_HIGH_CONFIDENCE
      ) {
        out.push({
          dexId,
          variant: t.variant,
          confidence: SELF_HEAL_REPORT_CONFIDENCE,
        });
        this.reported.add(dexId);
      }
    }
    return out;
  }
}

/**
 * POST a single self-heal report. Never throws; swallows all errors so a scan
 * teardown is never blocked by a failed report. Returns whether the POST
 * reported success (best-effort; not relied on).
 */
export async function reportSelfHeal(
  report: SelfHealReport,
  post: SelfHealPoster,
): Promise<{ ok: boolean }> {
  try {
    const res = await post("POST", SELF_HEAL_ENDPOINT, {
      pokemonId: report.dexId,
      variant: report.variant,
      confidence: report.confidence,
    });
    return { ok: !!res && res.ok === true };
  } catch {
    return { ok: false };
  }
}
